"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import { useAuth } from "@/context/auth-context";
import { AccountStatusNotification } from "@/components/account-status-notification";

type AppealSummary = {
  id: string;
  referenceId: string;
  status: string;
  submittedAt: string;
  resolvedAt?: string | null;
  resolution?: string | null;
};

type RestrictionResponse = {
  status: "warning" | "suspended" | "banned";
  reason?: string;
  expiresAt?: string | null;
  moderationActionId?: string | null;
  appeal?: AppealSummary | null;
};

type StatusResponse = { status: "ok" } | { status: "deleted" } | RestrictionResponse;

function isRestriction(data: StatusResponse): data is RestrictionResponse {
  return data.status === "warning" || data.status === "suspended" || data.status === "banned";
}

/**
 * Authenticated wrapper that polls /api/account/status once per page load
 * and renders <AccountStatusNotification> when the user has an active
 * warning or suspension. Hidden on auth pages and on the appeal page itself
 * (since the banner would just send them back to where they already are).
 *
 * Mounted from `app/client-layout.tsx` so it surfaces on every page.
 */
export function AccountStatusBanner() {
  const { isAuthenticated, user } = useAuth();
  const pathname = usePathname();
  const [data, setData] = useState<StatusResponse | null>(null);

  const hidden =
    !pathname ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/signup") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/verify-email") ||
    pathname.startsWith("/account-deleted") ||
    pathname.startsWith("/appeal") ||
    pathname.startsWith("/account/appeals");

  useEffect(() => {
    if (!isAuthenticated || !user?.id || hidden) {
      setData(null);
      return;
    }
    let cancelled = false;
    fetch("/api/account/status", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((json: StatusResponse | null) => {
        if (!cancelled) setData(json);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
    // Refetch when the route changes so the banner clears after the user
    // submits an appeal or an admin resolves it. We intentionally key only
    // on `pathname` + auth — the response itself is cached client-side.
  }, [isAuthenticated, user?.id, hidden, pathname]);

  if (hidden || !data || !isRestriction(data)) return null;

  const expiresAt = data.expiresAt ? new Date(data.expiresAt) : undefined;

  // Persist the dismissal server-side so the banner stays hidden across
  // refreshes. The notification hides itself locally first, so this is
  // fire-and-forget — if it fails the record simply reappears next load.
  const handleDismiss = () => {
    setData(null);
    fetch("/api/account/status/dismiss", {
      method: "POST",
      cache: "no-store",
    }).catch(() => {
      // Non-fatal: the banner is already hidden for this session.
    });
  };

  return (
    <div className="container mx-auto px-4 pt-4">
      <AccountStatusNotification
        status={data.status}
        reason={data.reason}
        expiresAt={expiresAt}
        moderationActionId={data.moderationActionId ?? null}
        appeal={data.appeal ?? null}
        onDismiss={handleDismiss}
      />
    </div>
  );
}
